/**
 * Unified API Hook
 * Single hook that consolidates all API operations
 * Integrates with UnifiedApiService and maintains backward compatibility with use-http
 * 
 * This hook provides:
 * - Entity-specific operations (CRUD)
 * - Custom API calls
 * - Loading states and error handling
 * - Store integration
 * - Backward compatibility with existing components
 */

import { useState, useCallback, useEffect } from 'react';
import { unifiedApiService } from '../services/UnifiedApiService';
import { appStore } from '../store/AppStore';

/**
 * Main unified API hook
 */
export const useUnifiedApi = (entityType = null, options = {}) => {
  const [state, setState] = useState({
    data: null,
    loading: false,
    error: null,
    total: 0
  });

  const {
    autoFetch = false,
    filters = {},
    pagination = { page: 0, rowsPerPage: 10 },
    updateStore = true
  } = options;

  // Get the appropriate service
  const service = entityType ? unifiedApiService.getService(entityType) : unifiedApiService;

  /**
   * Generic fetch all operation
   */
  const fetchAll = useCallback(async (customFilters = {}) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await service.fetchAll({ ...filters, ...customFilters }, { updateStore });
      setState(prev => ({
        ...prev,
        data: response,
        loading: false,
        total: response?.length || 0
      }));
      return response;
    } catch (error) {
      setState(prev => ({ ...prev, loading: false, error: error.message }));
      throw error;
    }
  }, [service, filters, updateStore]);

  /**
   * Generic fetch by ID operation
   */
  const fetchById = useCallback(async (id, customOptions = {}) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await service.fetchById(id, customOptions);
      setState(prev => ({ ...prev, data: response, loading: false }));
      return response;
    } catch (error) {
      setState(prev => ({ ...prev, loading: false, error: error.message }));
      throw error;
    }
  }, [service]);

  /**
   * Generic create operation
   */
  const create = useCallback(async (data) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await service.create(data);
      setState(prev => ({ ...prev, loading: false }));
      
      // Refresh data if auto-fetch is enabled
      if (autoFetch) {
        await fetchAll();
      }
      
      return response;
    } catch (error) {
      setState(prev => ({ ...prev, loading: false, error: error.message }));
      throw error;
    }
  }, [service, autoFetch, fetchAll]);

  /**
   * Generic update operation
   */
  const update = useCallback(async (id, data) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await service.update(id, data);
      setState(prev => ({ ...prev, loading: false }));
      
      // Refresh data if auto-fetch is enabled
      if (autoFetch) {
        await fetchAll();
      }
      
      return response;
    } catch (error) {
      setState(prev => ({ ...prev, loading: false, error: error.message }));
      throw error;
    }
  }, [service, autoFetch, fetchAll]);

  /**
   * Generic delete operation
   */
  const remove = useCallback(async (id) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await service.delete(id);
      setState(prev => ({ ...prev, loading: false }));
      
      // Refresh data if auto-fetch is enabled
      if (autoFetch) {
        await fetchAll();
      }
      
      return response;
    } catch (error) {
      setState(prev => ({ ...prev, loading: false, error: error.message }));
      throw error;
    }
  }, [service, autoFetch, fetchAll]);

  /**
   * Custom API call
   */
  const customCall = useCallback(async (endpoint, data = {}, options = {}) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await service.customCall(endpoint, data, options);
      setState(prev => ({ ...prev, loading: false }));
      return response;
    } catch (error) {
      setState(prev => ({ ...prev, loading: false, error: error.message }));
      throw error;
    }
  }, [service]);

  /**
   * Backward compatibility with use-http
   */
  const sendRequest = useCallback(async (requestConfig, responseHandler) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await unifiedApiService.request(requestConfig);
      setState(prev => ({ ...prev, loading: false }));
      
      if (responseHandler) {
        responseHandler(response);
      }
      
      return response;
    } catch (error) {
      setState(prev => ({ ...prev, loading: false, error: error.message }));
      throw error;
    }
  }, []);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch && entityType) {
      fetchAll();
    }
  }, [autoFetch, entityType, fetchAll]);

  return {
    // Data and state
    data: state.data,
    loading: state.loading,
    error: state.error,
    total: state.total,
    isLoading: state.loading, // Alias for backward compatibility
    
    // Operations
    fetchAll,
    fetchById,
    create,
    update,
    delete: remove,
    customCall,
    
    // Backward compatibility
    sendRequest,
    
    // Utility
    clearError: () => setState(prev => ({ ...prev, error: null })),
    clearData: () => setState(prev => ({ ...prev, data: null }))
  };
};

/**
 * Backward compatibility hook that mimics use-http
 */
export const useHttp = () => {
  return useUnifiedApi(null, { autoFetch: false });
};

/**
 * Specialized hooks for common entities
 */
export const useCorporates = (options = {}) => useUnifiedApi('corporate', options);
export const useStaff = (options = {}) => useUnifiedApi('staff', options);
export const useDrivers = (options = {}) => useUnifiedApi('driver', options);
export const useTrips = (options = {}) => useUnifiedApi('trip', options);
export const useRoutes = (options = {}) => useUnifiedApi('route', options);
export const useBookings = (options = {}) => useUnifiedApi('booking', options);
export const useDriverShifts = (options = {}) => useUnifiedApi('driverShift', options);
export const useAuth = (options = {}) => useUnifiedApi('auth', options);
export const useDepartments = (options = {}) => useUnifiedApi('department', options);
export const useSettings = (options = {}) => useUnifiedApi('settings', options);
export const useAdmins = (options = {}) => useUnifiedApi('admin', options);

export default useUnifiedApi;
